import { useState, useRef, useEffect, useCallback, KeyboardEvent } from 'react';
import SideNav from '../../components/shared/sidenav/SideNav';
import {
  EmptyState,
  MessageBubble,
  SendIcon,
  SpinnerIcon,
  ToolPill,
} from './BoxWidget';
import { Message, parseBlocks, SSEEvent, uid, XET_THEME } from './BoxChatModel';
import { getCookie, JWT_TOKEN } from '../../services/cookieService';
import environment from '../../environments/environment';

const BoxChat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [streamingMsgId, setStreamingMsgId] = useState('');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [toolActivity, setToolActivity] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, toolActivity]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 140) + 'px';
  };

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;

    const streamId = uid();
    setStreamingMsgId(streamId);
    const streamMsg: Message = {
      id: streamId,
      role: 'assistant',
      blocks: [{ type: 'text', content: '' }],
      rawText: '',
      ts: new Date(),
    };
    const userMsg: Message = {
      id: uid(),
      role: 'user',
      blocks: [{ type: 'text', content: text.trim() }],
      rawText: text.trim(),
      ts: new Date(),
    };

    const history = [...messages, userMsg].map((m) => ({
      role: m.role,
      content: m.rawText,
    }));
    setMessages((prev) => [...prev, userMsg, streamMsg]);

    setInput('');
    setLoading(true);
    setStreaming(true);
    setError(null);
    setToolActivity(null);
    if (inputRef.current) inputRef.current.style.height = 'auto';
    abortRef.current = new AbortController();

    try {
      const res = await fetch(`${environment.chatUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getCookie(JWT_TOKEN)}`,
        },
        body: JSON.stringify({ messages: history }),
        signal: abortRef.current.signal,
      });

      if (!res.ok || !res.body) throw new Error(`Server error ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';
      let usedTools: string[] = [];
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const evt: SSEEvent = JSON.parse(line.slice(6));
            console.log('Event: ', evt);
            if (evt.delta) {
              fullText += evt.delta;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === streamId
                    ? {
                        ...m,
                        rawText: fullText,
                        blocks: [{ type: 'text', content: fullText }],
                      }
                    : m
                )
              );
            }

            if (evt.text) {
              fullText = evt.text;
            }

            if (evt.tools) {
              usedTools = [...new Set([...usedTools, ...evt.tools])];
              setToolActivity(evt.tools);
            }

            if (evt.tool_end) {
              setToolActivity(null);
            }

            if (evt.done) {
              const parsedBlocks = parseBlocks(fullText);
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === streamId
                    ? {
                        ...m,
                        rawText: fullText,
                        blocks: parsedBlocks,
                        toolsUsed: usedTools,
                      }
                    : m
                )
              );
              setStreaming(false);
              setLoading(false);
            }

            if (evt.message) setError(evt.message);
          } catch (e) {
            console.log('Error: ', e);
          }
        }
      }
    } catch (e: unknown) {
      if ((e as Error).name !== 'AbortError') {
        setError('Connection failed. Is the chat server running?');
      }
    } finally {
      setLoading(false);
      setStreaming(false);
      setToolActivity(null);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleStop = () => {
    abortRef.current?.abort();
    setLoading(false);
    setStreaming(false);
    setToolActivity(null);
  };

  return (
    <div className="flex-row full-width full-height">
      <SideNav></SideNav>
      <div className="full-width">
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />

        <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html, body, #root { height: 100%; }
        @keyframes xspin  { to { transform: rotate(360deg); } }
        @keyframes xfade  { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes xblink { 0%,100% { opacity: 1; } 50% { opacity: 0; } }
        @keyframes xpulse { 0%,100% { transform: scale(1); opacity: 0.3; } 50% { transform: scale(1.08); opacity: 0.6; } }
        textarea { resize: none; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${XET_THEME.gray300}; border-radius: 4px; }
        button:focus-visible, textarea:focus-visible { outline: 2px solid ${XET_THEME.yellow}; outline-offset: 2px; }
      `}</style>

        <div
          style={{
            display: 'flex',
            height: '100vh',
            overflow: 'hidden',
            background: XET_THEME.offWhite,
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          {/* ════ MAIN AREA ════ */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minWidth: 0,
            }}
          >
            {/* Top bar */}
            <div
              style={{
                height: 60,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 24px',
                background: XET_THEME.white,
                borderBottom: `1px solid ${XET_THEME.gray100}`,
                boxShadow: '0 1px 4px rgba(13,27,75,0.06)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: XET_THEME.navy,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    XetAssist
                  </div>
                  {loading && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        fontSize: 11,
                        color: XET_THEME.yellow,
                        marginTop: 1,
                      }}
                    >
                      <SpinnerIcon color={XET_THEME.yellow} /> Thinking…
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 11,
                    color: XET_THEME.success,
                  }}
                >
                  <div
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: XET_THEME.success,
                      animation: 'xpulse 2s ease-in-out infinite',
                    }}
                  />
                  Live
                </div>
                <div
                  style={{
                    fontSize: 11,
                    padding: '3px 10px',
                    borderRadius: 20,
                    background: XET_THEME.gray50,
                    color: XET_THEME.gray500,
                    border: `1px solid ${XET_THEME.gray100}`,
                    fontWeight: 500,
                  }}
                >
                  {messages.length} message{messages.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>

            {/* Messages area */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '28px 0',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {messages.length === 0 ? (
                <EmptyState
                  onSelect={(p) => {
                    setInput(p);
                    inputRef.current?.focus();
                  }}
                />
              ) : (
                <div
                  style={{
                    maxWidth: 820,
                    width: '100%',
                    margin: '0 auto',
                    padding: '0 28px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 22,
                  }}
                >
                  {messages.map((msg) => (
                    <MessageBubble
                      key={msg.id}
                      msg={msg}
                      isStreaming={msg.id === streamingMsgId && streaming}
                    />
                  ))}

                  {toolActivity && toolActivity.length > 0 && (
                    <div style={{ paddingLeft: 42 }}>
                      <ToolPill tools={toolActivity} />
                    </div>
                  )}

                  {error && (
                    <div
                      style={{
                        padding: '10px 16px',
                        borderRadius: 10,
                        background: '#FEF2F2',
                        border: '1px solid #FECACA',
                        fontSize: 12,
                        color: XET_THEME.danger,
                      }}
                    >
                      ⚠ {error}
                    </div>
                  )}

                  <div ref={bottomRef} />
                </div>
              )}
            </div>

            {/* Input bar */}
            <div
              style={{
                flexShrink: 0,
                padding: '16px 28px 22px',
                background: XET_THEME.white,
                borderTop: `1px solid ${XET_THEME.gray100}`,
              }}
            >
              <div style={{ maxWidth: 820, margin: '0 auto' }}>
                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-end',
                    background: XET_THEME.white,
                    border: `1.5px solid ${XET_THEME.gray100}`,
                    borderRadius: 14,
                    padding: '10px 10px 10px 18px',
                    boxShadow: '0 2px 12px rgba(13,27,75,0.06)',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                  onFocusCapture={(e) => {
                    const el = e.currentTarget as HTMLDivElement;
                    el.style.borderColor = XET_THEME.navy;
                    el.style.boxShadow = `0 0 0 3px rgba(13,27,75,0.08)`;
                  }}
                  onBlurCapture={(e) => {
                    const el = e.currentTarget as HTMLDivElement;
                    el.style.borderColor = XET_THEME.gray100;
                    el.style.boxShadow = '0 2px 12px rgba(13,27,75,0.06)';
                  }}
                >
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder='Ask anything — say "as a table" or "as a chart" for structured output'
                    rows={1}
                    disabled={loading}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontSize: 14,
                      fontFamily: "'Poppins', sans-serif",
                      color: XET_THEME.text,
                      lineHeight: 1.6,
                      resize: 'none',
                      overflow: 'hidden',
                      maxHeight: 140,
                      minHeight: 24,
                    }}
                  />

                  {loading ? (
                    <button
                      onClick={handleStop}
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        flexShrink: 0,
                        background: '#FEF2F2',
                        border: '1px solid #FECACA',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: XET_THEME.danger,
                        fontSize: 14,
                      }}
                      title="Stop"
                    >
                      ■
                    </button>
                  ) : (
                    <button
                      onClick={() => sendMessage(input)}
                      disabled={!input.trim()}
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        flexShrink: 0,
                        background: input.trim()
                          ? XET_THEME.yellow
                          : XET_THEME.gray100,
                        border: 'none',
                        cursor: input.trim() ? 'pointer' : 'default',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: input.trim()
                          ? XET_THEME.navy
                          : XET_THEME.gray300,
                        transition: 'all 0.15s',
                        boxShadow: input.trim()
                          ? '0 2px 8px rgba(251,188,4,0.4)'
                          : 'none',
                      }}
                      title="Send (Enter)"
                    >
                      <SendIcon />
                    </button>
                  )}
                </div>

                <div
                  style={{
                    marginTop: 8,
                    textAlign: 'center',
                    fontSize: 10,
                    color: XET_THEME.gray300,
                    letterSpacing: '0.02em',
                  }}
                >
                  Enter to send · Shift+Enter for new line
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoxChat;
